import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "Who is Wonder Time for?",
    a: "Indian parents of children aged 3 to 5. If your child is a little younger or older, they'll still enjoy most activities — but the sweet spot is 3–5.",
  },
  {
    q: "How does the daily delivery work?",
    a: "Every morning at 8:00 AM IST for 21 days, you'll receive one WhatsApp message from us with a beautifully-written activity — a small experiment, a story, or a question game. That's it. No app, no login, no clutter.",
  },
  {
    q: "How much time do I need each day?",
    a: "10 to 15 minutes. That's the whole idea. We designed this for real Indian families with real mornings — school runs, tiffins, and all.",
  },
  {
    q: "Do I need any special materials?",
    a: "No. Every activity uses things already at home — a katori, some rice, an old newspaper, a torch. If ever we suggest anything at all, it's under ₹50 and can wait for the next kirana visit.",
  },
  {
    q: "Is this screen time for my child?",
    a: "Absolutely not. You (the parent) receive the WhatsApp. Your child never touches a screen. Every activity is hands-on, offline, and together.",
  },
  {
    q: "When does the next cohort start?",
    a: "Cohorts start every Monday. Once you enroll, you'll receive a warm welcome message with the exact date and a quick 'day zero' checklist.",
  },
  {
    q: "What if it isn't right for us?",
    a: "Message us on WhatsApp within the first 3 days and we'll refund you in full — no long forms, no questions.",
  },
];

function Row({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="border-b border-ink/15"
      data-testid={`faq-item-${i}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-6 sm:py-8 text-left group"
        data-testid={`faq-toggle-${i}`}
        aria-expanded={open}
      >
        <span className="font-serif text-xl sm:text-2xl lg:text-3xl text-ink pr-6 leading-tight">
          {q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="shrink-0 w-11 h-11 rounded-full border border-ink/25 flex items-center justify-center group-hover:bg-ink group-hover:text-cream group-hover:border-ink transition-colors"
        >
          <Plus className="w-5 h-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-8 pr-14 sm:pr-24 text-base sm:text-lg text-ink/75 leading-relaxed max-w-3xl">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  return (
    <section
      id="faq"
      className="py-24 sm:py-32 bg-cream"
      data-testid="faq-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14"
        >
          <div className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-terracotta mb-4">
            Small worries, warm answers
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink max-w-3xl">
            Everything a <br />
            <span className="italic">parent wonders</span> about.
          </h2>
        </motion.div>

        <div>
          {faqs.map((f, i) => (
            <Row key={f.q} q={f.q} a={f.a} i={i} />
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 sm:mt-14 text-base sm:text-lg text-ink/75 leading-relaxed max-w-2xl"
          data-testid="faq-outro"
        >
          Still unsure? Send us a WhatsApp — a real human from{" "}
          <span className="italic text-terracotta">Team Curious Roots</span>{" "}
          will reply, usually within an hour.
        </motion.p>
      </div>
    </section>
  );
}
